import { Box, Typography, styled } from '@mui/material';
import React, { useState } from 'react';
import AddButton from 'ui/Buttons/AddButton';
import SearchBar from 'ui/SearchBar/SearchBar';
import { checkStringMatch } from 'routes/Library/helpers';
import StyBoxAdmin from 'routes/Admin/shared/StyBoxAdmin';
import Loading from 'components/Loading';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import {
    useGetProjects,
    Project,
} from 'routes/BudgetModule/shared/useGetProjects';
import ProjectsGrid from './ProjectsGrid/ProjectsGrid';
import CreateProjectModal from './ProjectsModals/CreateProjectModal';
import StyHeaderBudgetSettings from '../shared/StyHeaderBudgetSettings';
import { useTranslation } from 'react-i18next';

const StyGridTitleContainer = styled(Box, {
    name: 'StyGridTitleContainer',
})(({ theme }) => ({
    display: 'flex',
    gap: '2em',
    alignSelf: 'center',
    alignItems: 'center',
    color: theme.palette.primary.main,
}));

const ManageProjects = (): React.ReactElement => {
    const { t } = useTranslation();
    const { isLoading, projects } = useGetProjects();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [search, setSearch] = useState<string>('');

    const filteredProjects = projects.filter((project: Project) =>
        checkStringMatch(project.name, search),
    );

    const handleSearchChange = (value: string): void => {
        setSearch(value);
        trackEventBudget(TRACKABLE_EVENTS.budget.settings.projects.search);
    };

    const createProjectModal = (
        <CreateProjectModal
            showModal={showCreateModal}
            setShowModal={setShowCreateModal}
            projects={projects}
            isLoading={isLoading}
        />
    );

    return (
        <StyBoxAdmin>
            <StyHeaderBudgetSettings>
                <StyGridTitleContainer>
                    <Typography
                        variant="h4"
                        sx={{ color: (theme) => theme.palette.darkGrey }}
                    >
                        {t('adminBudgetSpace.projectsList')}
                    </Typography>
                    <SearchBar
                        width="15rem"
                        handleChange={(value) => handleSearchChange(value)}
                        placeholder={t('adminBudgetSpace.searchProjects')}
                    />
                </StyGridTitleContainer>

                <AddButton
                    onClick={() => {
                        trackEventBudget(
                            TRACKABLE_EVENTS.budget.settings.projects.add,
                        );
                        setShowCreateModal(true);
                    }}
                    text={t('adminBudgetSpace.addProject')}
                    data-testid="add-project-button"
                />
            </StyHeaderBudgetSettings>
            {isLoading ? (
                <Box sx={{ flex: 15 }}>
                    <Loading />
                </Box>
            ) : (
                <ProjectsGrid projects={filteredProjects} />
            )}
            {showCreateModal && createProjectModal}
        </StyBoxAdmin>
    );
};

export default ManageProjects;
